import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    await this.#waitForHydration();
    if (!this.isConnected) return;

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('touchstart', this.#pauseForTouch, { passive: true });
    this.addEventListener('touchend', this.#resumeAfterTouch, { passive: true });
    this.addEventListener('touchcancel', this.#resumeAfterTouch, { passive: true });

    if (this.hasAttribute('data-product-marquee')) {
      this.addEventListener('scroll', this.#handleProductScroll, { passive: true });
      await this.#initializeProductLoop();
      return;
    }

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }

    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#speedUp);
    this.removeEventListener('touchstart', this.#pauseForTouch);
    this.removeEventListener('touchend', this.#resumeAfterTouch);
    this.removeEventListener('touchcancel', this.#resumeAfterTouch);
    this.removeEventListener('scroll', this.#handleProductScroll);

    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
    if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
  }

  async #waitForHydration() {
    if (this.hasAttribute('data-product-marquee') && window.Shopify && window.Shopify.designMode) {
      const section = this.closest('.shopify-section');

      if (section && section.dataset.hydrated !== 'true') {
        await new Promise((resolve) => {
          const observer = new MutationObserver(() => {
            if (section.dataset.hydrated !== 'true') return;
            observer.disconnect();
            resolve();
          });

          observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
          setTimeout(() => {
            observer.disconnect();
            resolve();
          }, 3000);
        });
      }
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #loopWidth = 0;
  #scrollFrame = null;
  #resumeTimer = null;
  #isTouching = false;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #handleFocusIn = () => {
    requestAnimationFrame(() => {
      if (this.querySelector(':focus-visible')) this.#slowDown();
    });
  };

  #pauseForTouch = () => {
    if (this.hasAttribute('data-product-marquee')) {
      this.#isTouching = true;
      if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
      this.refs.wrapper.style.animationPlayState = 'paused';
      return;
    }

    this.refs.wrapper.style.animationPlayState = 'paused';
  };

  #resumeAfterTouch = () => {
    if (this.hasAttribute('data-product-marquee')) {
      this.#isTouching = false;
      this.#scheduleProductResume();
      return;
    }

    this.refs.wrapper.style.animationPlayState = 'running';
  };

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const marqueeWidth = this.getBoundingClientRect().width;
    const marqueeItemsWidth = marqueeItems[0]?.getBoundingClientRect().width ?? 0;
    const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
    this.#marqueeWidth = marqueeWidth;

    return {
      numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth)),
      isHorizontalResize,
    };
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);

    if (this.hasAttribute('data-product-marquee')) {
      await this.#initializeProductLoop();
    } else {
      this.#restartAnimation();
    }
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #cloneIndex = 0;

  #prepareClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    if (!this.hasAttribute('data-product-marquee')) {
      clone.setAttribute('inert', '');
      for (const element of clone.querySelectorAll('[id]')) element.removeAttribute('id');
      return;
    }

    clone.removeAttribute('inert');
    const suffix = `--marquee-copy-${++this.#cloneIndex}`;
    const idMap = new Map();

    for (const element of clone.querySelectorAll('[id]')) {
      const oldId = element.id;
      const newId = `${oldId}${suffix}`;
      idMap.set(oldId, newId);
      element.id = newId;
    }

    for (const element of clone.querySelectorAll('[for], [aria-controls], [aria-describedby], [aria-labelledby]')) {
      for (const attribute of ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby']) {
        const value = element.getAttribute(attribute);
        if (!value) continue;
        element.setAttribute(
          attribute,
          value
            .split(' ')
            .map((id) => idMap.get(id) || id)
            .join(' ')
        );
      }
    }

    for (const element of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
      element.setAttribute('tabindex', '-1');
    }
  }

  #duplicateContent() {
    const { content, wrapper } = this.refs;

    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }

    if (this.hasAttribute('data-product-marquee')) {
      const beforeClone = /** @type {HTMLElement} */ (content.cloneNode(true));
      const afterClone = /** @type {HTMLElement} */ (content.cloneNode(true));
      this.#prepareClone(beforeClone);
      this.#prepareClone(afterClone);
      wrapper.insertBefore(beforeClone, content);
      wrapper.appendChild(afterClone);
      return;
    }

    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    this.#prepareClone(clone);
    wrapper.appendChild(clone);
  }

  async #initializeProductLoop() {
    this.setAttribute('data-scroll-loop-ready', '');
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const groups = this.refs.wrapper.children;
    if (groups.length < 3) return;

    this.#loopWidth = groups[1].offsetLeft - groups[0].offsetLeft;
    if (this.#loopWidth <= 0) return;

    this.style.setProperty('--marquee-loop-distance', `-${this.#loopWidth}px`);
    this.scrollLeft = this.#loopWidth;
    this.refs.wrapper.style.animationPlayState = 'running';
  }

  #handleProductScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#recenterProductLoop();
      if (!this.#isTouching) this.#scheduleProductResume();
    });
  };

  #scheduleProductResume() {
    if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
    if (this.#isTouching) return;

    this.#resumeTimer = window.setTimeout(() => {
      this.refs.wrapper.style.animationPlayState = 'running';
      this.#resumeTimer = null;
    }, 250);
  }

  #recenterProductLoop() {
    if (this.#loopWidth <= 0) return;

    const lowerBoundary = this.#loopWidth * 0.5;
    const upperBoundary = this.#loopWidth * 1.5;
    let nextPosition = this.scrollLeft;

    while (nextPosition < lowerBoundary) nextPosition += this.#loopWidth;
    while (nextPosition >= upperBoundary) nextPosition -= this.#loopWidth;

    if (Math.abs(nextPosition - this.scrollLeft) > 0.5) {
      this.scrollLeft = nextPosition;
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      this.#prepareClone(clone);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
